import { COMMUNITY_SLACK_URL } from "@/utils/community-support";
import { usePylonEnabled } from "@/utils/hooks/use-pylon-enabled";
import { ErrorMessage } from "@/components/Message";
import { Icon } from "@/components/icons/Icon";
import { Button } from "@/components/interactive/Button";
import { CopyButton } from "@/components/interactive/CopyButton";
import { ButtonV2, LinkButtonV2 } from "@/components/interactive/ButtonV2";
import { Layout } from "@/components/layout/Layout";
import { ExtremelySimpleLayout } from "@/components/layout/Layout";
import { BreadCrumb } from "@/components/navigation/BreadCrumbs";
import { ProjectNavbar } from "@/components/navigation/ProjectNavbar";
import { H1 } from "@/components/typography";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import { datadogRum } from "@datadog/browser-rum";
import { ErrorComponent } from "next/dist/client/components/error-boundary";
import { useRouter } from "next/router";
import { useEffect, useMemo, type FC } from "react";

interface ChalkLayoutErrorContentProps {
  error: unknown;
  showBackButton?: boolean;
}

const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(typeof error === "string" ? error : String(error));

const ChalkLayoutErrorContent: FC<ChalkLayoutErrorContentProps> = ({
  error,
  showBackButton = true,
}) => {
  const router = useRouter();
  const pylonEnabled = usePylonEnabled();
  const normalizedError = useMemo(() => normalizeError(error), [error]);
  const errorReport = `I encountered an unrecoverable rendering error [${normalizedError.message || "no error message provided"}] on the page [${router.asPath}]. Could you please investigate?`;

  useEffect(() => {
    datadogRum.addError(normalizedError, { page: router.asPath });
  }, [normalizedError, router.asPath]);

  return (
    <div className={"space-y-4"}>
      <div>
        {showBackButton && <BreadCrumb onClick={router.back}>Back</BreadCrumb>}
        <H1>Error</H1>
      </div>
      <ErrorMessage title={"An error occurred while rendering this page"}>
        <div className={"space-y-4"}>
          <div
            className={"whitespace-pre-wrap"}
          >{`The error message was [${normalizedError.message || "no message provided"}]`}</div>
          <div className={"flex gap-2"}>
            <ButtonV2
              type={"button"}
              onClick={router.reload}
              size="sm"
              variant="secondary"
              leftIcon="retry"
            >
              Reload page
            </ButtonV2>
            {pylonEnabled ? (
              <ButtonV2
                type="button"
                onClick={() => window.Pylon?.("showNewMessage", errorReport)}
                size="sm"
                variant="secondary"
                leftIcon="question"
              >
                Contact support
              </ButtonV2>
            ) : (
              <LinkButtonV2
                href={COMMUNITY_SLACK_URL}
                openInNewTab
                size="sm"
                variant="secondary"
                leftIcon="messageSquare"
              >
                Community Slack
              </LinkButtonV2>
            )}
          </div>
          {!pylonEnabled && (
            <div className="flex items-start gap-2">
              <pre className="min-w-0 flex-1 whitespace-pre-wrap break-words font-mono text-xs">
                {errorReport}
              </pre>
              <CopyButton value={errorReport} tooltipText="Copy error report" />
            </div>
          )}
        </div>
      </ErrorMessage>
    </div>
  );
};

export const ChalkLayoutErrorComponent: ErrorComponent = ({ error }) => {
  return <ChalkLayoutErrorContent error={error} />;
};

export const ChalkLayoutErrorComponentWithoutBack: ErrorComponent = ({ error }) => {
  return <ChalkLayoutErrorContent error={error} showBackButton={false} />;
};

/**
 * Fallback for any tree that does NOT sit under the authenticated
 * NetworkingWrapper — marketing pages, and the pages served by
 * `UnauthenticatedNetworkingWrapper` (`/login`, `/signup`, `/cli`,
 * `/onboarding`, `/shared`).
 *
 * ExtremelySimpleLayout is load-bearing, not a stylistic choice: `Layout` calls
 * `useAmplitudeBaseIdentify`, whose `useRouteAndTeamInfo` provider only the
 * authenticated wrapper mounts. A fallback that throws is worse than no
 * fallback — the throw escapes to the outermost boundary and the visitor gets a
 * blank page reporting the fallback's own crash instead of the real error.
 */
export const ChalkSimpleErrorComponent: ErrorComponent = (props) => {
  return (
    <ExtremelySimpleLayout>
      <ChalkLayoutErrorComponent {...props} />
    </ExtremelySimpleLayout>
  );
};

export const ChalkGlobalErrorComponent: ErrorComponent = (props) => {
  const { projectId, environmentId } = useRouteInfo();

  return (
    <Layout
      title={"Error"}
      loggedIn
      navBar={projectId != null && environmentId != null ? <ProjectNavbar /> : undefined}
    >
      <ChalkLayoutErrorComponent {...props} />
    </Layout>
  );
};
